import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * ScrollManager — restores scroll position when navigating BACK (POP) and
 * scrolls to top when navigating FORWARD to a new route (PUSH).
 *
 * Strategy:
 *  • On every scroll, debounce and save the current scrollY for the current
 *    location.key into sessionStorage.
 *  • When the location changes:
 *      - If the navigation type is POP (back/forward button), restore the
 *        previously-saved scrollY for the new location.key.
 *      - Otherwise (PUSH / REPLACE), scroll to (0,0).
 *  • We use location.key (not pathname) because React Router gives a stable
 *    unique key per history entry, so going back from /cards/X to /sets/Y
 *    restores the same Y we left, even if /sets/Y has been visited multiple
 *    times with different filters.
 */
export default function ScrollManager() {
  const location = useLocation();
  const navType = useNavigationType();
  const debounceRef = useRef(null);

  // Save scroll on scroll (debounced)
  useEffect(() => {
    const onScroll = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        try {
          sessionStorage.setItem(`scroll:${location.key}`, String(window.scrollY));
        } catch {
          /* sessionStorage may throw in private mode */
        }
      }, 80);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [location.key]);

  // Restore / reset on navigation
  useEffect(() => {
    if (navType === "POP") {
      const raw = sessionStorage.getItem(`scroll:${location.key}`);
      const y = raw ? parseInt(raw, 10) : 0;
      if (y === 0) {
        window.scrollTo(0, 0);
        return;
      }
      // Pages fetch data + render images async, so scrollHeight grows over time.
      // We watch it with a ResizeObserver and scroll as soon as the document
      // is tall enough — capped at 4s total to avoid lingering effects.
      let done = false;
      const target = y;
      const attempt = () => {
        if (done) return;
        const maxScrollable = document.documentElement.scrollHeight - window.innerHeight;
        if (maxScrollable >= target) {
          done = true;
          window.scrollTo(0, target);
        }
      };
      const ro = new ResizeObserver(attempt);
      ro.observe(document.documentElement);
      attempt(); // initial try
      const timeout = setTimeout(() => {
        done = true;
        // Final attempt — may end up clipped, but that's the best we can do.
        const maxScrollable = document.documentElement.scrollHeight - window.innerHeight;
        window.scrollTo(0, Math.min(target, Math.max(0, maxScrollable)));
      }, 4000);
      return () => {
        done = true;
        ro.disconnect();
        clearTimeout(timeout);
      };
    }
    // PUSH or REPLACE → fresh page, scroll to top.
    window.scrollTo(0, 0);
  }, [location.key, navType]);

  return null;
}
